import React, { useState } from 'react';
import {
  TrendingUp,
  ShieldCheck,
  PieChart,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Lock
} from 'lucide-react';

export default function Investors() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    investorType: 'Individual Investor',
    budget: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      const response = await fetch(
        'https://website.backend.tekvancesolutions.co.ke/api/mbogi-capital/investors',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fullName: formData.name,
            email: formData.email,
            investorType: formData.investorType,
            targetAllocation: formData.budget,
            additionalInterests: formData.message
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit investor request');
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        investorType: 'Individual Investor',
        budget: '',
        message: ''
      });
    } catch (error) {
      console.error('❌ Investor submission error:', error.message);
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-xl">
          <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Investor request received
          </h2>

          <p className="text-slate-500 text-lg mb-8 leading-relaxed">
            Thank you for your interest in MbogiCapital. Our investment relations
            team will review your submission and share the investor deck together
            with the next steps.
          </p>

          <div className="bg-slate-50 border border-slate-100 rounded-3xl p-6 mb-8 text-left">
            <p className="text-sm text-slate-500 mb-2">
              What happens next
            </p>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#c07d3e] mt-0.5 flex-shrink-0" />
                <span>Your investor interest has been recorded.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#c07d3e] mt-0.5 flex-shrink-0" />
                <span>Our team will reach out with the investor deck.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#c07d3e] mt-0.5 flex-shrink-0" />
                <span>You may also receive updates on upcoming funding rounds.</span>
              </li>
            </ul>
          </div>

          <button
            onClick={() => setSubmitted(false)}
            className="text-[#c07d3e] font-bold hover:underline"
          >
            Submit another response
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans text-slate-900">
      <section className="pt-20 pb-16 px-6 border-b border-slate-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block bg-orange-50 text-[#c07d3e] text-xs font-bold px-3 py-1 rounded-full mb-6 uppercase tracking-widest">
              Investment Opportunity
            </span>

            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
              Invest in the <span className="text-[#c07d3e]">Next Wave</span> of African Cinema.
            </h1>

            <p className="text-slate-500 text-lg md:text-xl leading-relaxed mb-8">
              MbogiCapital provides a transparent, blockchain-secured gateway to
              fund high-growth African film projects and share in global revenue
              distributions.
            </p>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                SEC Compliant Framework
              </div>
              <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                <Lock className="w-5 h-5 text-green-500" />
                Escrow Protected Funds
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-[3rem] p-8 border border-slate-100 shadow-sm relative overflow-hidden">
            <div className="relative z-10">
              <p className="text-sm font-bold text-slate-400 uppercase tracking-tighter mb-2">
                Market Forecast
              </p>

              <p className="text-3xl font-bold mb-6">
                $20B+ <span className="text-sm font-normal text-slate-500">Projected Market by 2030</span>
              </p>

              <div className="space-y-4">
                {[
                  { label: 'Streaming Growth', val: '85%' },
                  { label: 'Diaspora Engagement', val: '92%' },
                  { label: 'Production Efficiency', val: '40%' }
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs font-bold mb-1">
                      <span>{item.label}</span>
                      <span className="text-[#c07d3e]">{item.val}</span>
                    </div>
                    <div className="h-2 bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#c07d3e] rounded-full"
                        style={{ width: item.val }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-orange-100 rounded-full blur-3xl opacity-50" />
          </div>
        </div>
      </section>

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Why Invest with Mbogi?
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto">
            We combine traditional film finance with modern decentralized
            technology to maximize returns and transparency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: 'Fractional Ownership',
              desc: 'Invest in high-budget productions through tokenized assets starting at accessible entry points.',
              icon: <PieChart />
            },
            {
              title: 'Automated Distributions',
              desc: 'Smart contracts distribute streaming and box office royalties directly to your wallet in real-time.',
              icon: <TrendingUp />
            },
            {
              title: 'Diversified Portfolio',
              desc: 'Spread your capital across multiple genres and regions within the African continent to mitigate risk.',
              icon: <BarChart3 />
            }
          ].map((card, i) => (
            <div
              key={i}
              className="p-10 rounded-[2.5rem] bg-slate-50 hover:bg-white border border-transparent hover:border-slate-100 hover:shadow-xl transition-all group"
            >
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-[#c07d3e] mb-8 shadow-sm group-hover:bg-[#c07d3e] group-hover:text-white transition-colors">
                {card.icon}
              </div>
              <h3 className="text-xl font-bold mb-4">{card.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 bg-slate-900 text-white rounded-t-[4rem]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              Join the Investor Circle.
            </h2>

            <p className="text-slate-400 text-lg mb-10">
              Complete the form to receive our latest investor deck, upcoming
              project pipeline, and white-list access for our next funding round.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle2 className="text-[#c07d3e] w-6 h-6 flex-shrink-0" />
                <p className="text-slate-300 font-medium">
                  Early access to Tier-1 film productions.
                </p>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="text-[#c07d3e] w-6 h-6 flex-shrink-0" />
                <p className="text-slate-300 font-medium">
                  Detailed quarterly transparency reports.
                </p>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="text-[#c07d3e] w-6 h-6 flex-shrink-0" />
                <p className="text-slate-300 font-medium">
                  Invitations to exclusive set visits and premieres.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[3rem] p-8 md:p-12 text-slate-900 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Full Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    placeholder="John Doe"
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all"
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    placeholder="john@example.com"
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all"
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Investor Type
                </label>
                <select
                  value={formData.investorType}
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all appearance-none"
                  onChange={(e) => setFormData({ ...formData, investorType: e.target.value })}
                >
                  <option value="Individual Investor">Individual Investor</option>
                  <option value="Angel Investor">Angel Investor</option>
                  <option value="VC Fund">VC Fund</option>
                  <option value="Private Equity">Private Equity</option>
                  <option value="Family Office">Family Office</option>
                  <option value="Strategic Partner">Strategic Partner</option>
                  <option value="Institutional Investor">Institutional Investor</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Target Allocation (USD)
                </label>
                <input
                  type="text"
                  required
                  value={formData.budget}
                  placeholder="$10,000 - $50,000"
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all"
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Additional Interests
                </label>
                <textarea
                  rows="3"
                  value={formData.message}
                  placeholder="Tell us about your investment goals..."
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all resize-none"
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </div>

              {errorMessage && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-5 rounded-2xl font-extrabold flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-100 ${
                  loading
                    ? 'bg-[#d7a06f] text-white cursor-not-allowed'
                    : 'bg-[#c07d3e] hover:bg-[#a66b34] text-white'
                }`}
              >
                {loading ? 'Submitting...' : 'Request Investor Deck'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>

              <p className="text-[10px] text-center text-slate-400 italic">
                By submitting, you agree to receive communications regarding
                investment opportunities.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}